/**
 * Almari · wardrobe carbon footprint.
 *
 * Textile LCA dataset (per-garment kgCO2e, manufacturing + transport + EoL):
 *   cotton tee/shirt: 8 kg          jeans (cotton denim): 22 kg
 *   silk kurta:       30 kg         wool blazer:          50 kg
 *   linen kurta:      10 kg         polyester athleisure: 22 kg
 *   leather shoe:     14 kg         sneakers (synth):     12 kg
 *   accessory:        2 kg
 *
 * Sources: Quantis 2018, EU JRC textile LCA 2020, ENS Lyon 2023. Indicative; not
 * audit-grade. Caveat surfaced in the UI.
 */
import { store } from '../store.ts';
import type { ClothingItem } from '../types.ts';

interface FootprintRow {
  readonly category: string;
  readonly count: number;
  readonly co2_kg: number;
}

const PER_ITEM_CO2_KG: Record<string, number> = {
  shirts: 8,
  pants: 22,
  shoes: 12,
  ethnic_kurta: 10,
  ethnic_sherwani: 35,
  outerwear: 50,
  accessories: 2,
  innerwear: 4,
  sportswear: 22,
};

function brandMultiplier(brand: string): number {
  // Cheap fast-fashion brands carry a 1.25x multiplier; sustainable premium 0.85x.
  const lo = brand.toLowerCase();
  if (/(zara|h&m|shein|forever 21|roadster)/.test(lo)) return 1.25;
  if (/(fabindia|anokhi|patagonia)/.test(lo)) return 0.85;
  return 1.0;
}

function rowFor(items: ClothingItem[]): FootprintRow[] {
  const acc = new Map<string, { count: number; co2: number }>();
  for (const it of items) {
    const base = PER_ITEM_CO2_KG[it.category] ?? 8;
    const c = base * brandMultiplier(it.brand);
    const cur = acc.get(it.category) ?? { count: 0, co2: 0 };
    cur.count += 1;
    cur.co2 += c;
    acc.set(it.category, cur);
  }
  return [...acc.entries()]
    .map(([k, v]) => ({ category: k, count: v.count, co2_kg: Math.round(v.co2 * 10) / 10 }))
    .sort((a, b) => b.co2_kg - a.co2_kg);
}

export class AlmariCarbonFootprint extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary.items);
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(items: ClothingItem[]): void {
    if (items.length === 0) {
      this.#shadow.innerHTML = '<style>:host{display:none;}</style>';
      return;
    }
    const rows = rowFor(items);
    const total = rows.reduce((s, r) => s + r.co2_kg, 0);

    // Reference: average urban Indian flies ~1 ton CO2/yr in domestic+intl
    // Mumbai-Goa-return ~ 200 kg. Frame wardrobe in those terms.
    const flights = (total / 200).toFixed(1);
    const trees = Math.round(total / 21); // trees absorb ~21 kg/yr

    const max = Math.max(...rows.map((r) => r.co2_kg), 1);
    const bars = rows.slice(0, 6).map((r) => `
      <tr>
        <td class="cat">${escapeHtml(label(r.category))}</td>
        <td class="bar"><span style="width:${Math.round((r.co2_kg / max) * 100)}%"></span></td>
        <td class="co2">${r.co2_kg.toFixed(0)} kg</td>
      </tr>
    `).join('');

    this.#shadow.innerHTML = `
<style>
:host { display: block; }
.head { padding-left: 4px; margin-bottom: 12px; display: grid; gap: 4px; }
.eyebrow { font-family: var(--mono); font-size: 9px; letter-spacing: 0.42em; text-transform: uppercase; color: oklch(45% 0.16 145); }
.title { font-family: var(--display); font-style: italic; font-size: 22px; color: var(--ink); font-weight: 600; letter-spacing: -0.005em; }
.card {
  background: var(--paper-card); border: 1px solid var(--rule); border-radius: 8px;
  border-left: 3px solid oklch(45% 0.16 145); padding: 18px 16px 14px; isolation: isolate;
}
.kpi { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 16px; }
.kpi-val { font-family: var(--display); font-size: 38px; font-weight: 700; color: oklch(40% 0.16 145); letter-spacing: -0.02em; font-feature-settings: 'tnum' 1; line-height: 1; }
.kpi-label { font-family: var(--mono); font-size: 9px; letter-spacing: 0.32em; text-transform: uppercase; color: var(--ink-faint); margin-top: 6px; }
.equiv { font-family: var(--display); font-style: italic; font-size: 15px; color: var(--ink-dim); padding: 10px 0 14px; border-top: 1px dashed var(--rule); margin-bottom: 14px; text-wrap: pretty; }
table { width: 100%; border-collapse: collapse; }
td { padding: 7px 0; font-family: var(--mono); font-size: 10px; letter-spacing: 0.04em; }
.cat { color: var(--ink); width: 38%; }
.bar { width: 38%; padding-right: 8px; }
.bar span {
  display: block; height: 5px;
  background: linear-gradient(90deg, oklch(50% 0.13 145), oklch(64% 0.12 140));
  border-radius: 3px; transition: width 600ms cubic-bezier(0.32, 0.72, 0, 1);
}
.co2 { color: oklch(40% 0.16 145); font-weight: 700; text-align: right; font-feature-settings: 'tnum' 1; }
.disclaimer { font-family: var(--mono); font-size: 8.5px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--ink-faint); margin-top: 14px; padding-top: 12px; border-top: 1px dashed var(--rule); text-align: right; }
</style>
<header class="head">
  <span class="eyebrow">Wardrobe carbon · indicative LCA</span>
  <h3 class="title">${total.toFixed(0)} kg CO2e in your almari.</h3>
</header>
<div class="card">
  <div class="kpi">
    <div>
      <div class="kpi-val">${flights}</div>
      <div class="kpi-label">return BOM ↔ GOA</div>
    </div>
    <div>
      <div class="kpi-val">${trees}</div>
      <div class="kpi-label">tree-years to absorb</div>
    </div>
  </div>
  <div class="equiv">Tumhari almari ka asar — utna jitna ${flights} Bombay-Goa flight, ya ${trees} ped saal bhar absorb kare.</div>
  <table>${bars}</table>
  <div class="disclaimer">LCA baseline · Quantis 2018, JRC 2020. ±25% range typical.</div>
</div>
`;
  }
}

function label(c: string): string {
  return c.replace('_', ' ').replace(/^./, (m) => m.toUpperCase());
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

customElements.define('almari-carbon-footprint', AlmariCarbonFootprint);
